import * as React from 'react';
import { useList } from '@refinedev/core';
import {
  Activity,
  CreditCard,
  GraduationCap,
  Mail,
  Shield,
  Sparkles,
  Ticket,
  UserCheck,
  XCircle,
} from 'lucide-react';
import type { SegmentGroup } from '@masters-club/validation';
import { Button } from '@/components/ui/button';

interface Preset {
  label: string;
  icon: React.ReactNode;
  group: SegmentGroup;
}

// ── Статические пресеты: профиль / активность / платежи ──────────────────
const STATIC_PRESETS: Preset[] = [
  {
    label: 'Активна подписка',
    icon: <Shield className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'subscriptionStatus', operator: 'eq', value: 'ACTIVE' }],
    },
  },
  {
    label: 'Активен тариф',
    icon: <Sparkles className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [
        { field: 'subscriptionStatus', operator: 'eq', value: 'ACTIVE' },
        { field: 'subscriptionPlanId', operator: 'isSet' },
      ],
    },
  },
  {
    label: 'Онлайн за 7 дней',
    icon: <Activity className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'onlineWithinDays', operator: 'withinLastDays', value: 7 }],
    },
  },
  {
    label: 'Есть платежи',
    icon: <CreditCard className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'purchaseCount', operator: 'gt', value: 0 }],
    },
  },
  {
    label: 'Email подтверждён',
    icon: <Mail className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'isEmailVerified', operator: 'eq', value: true }],
    },
  },
  {
    label: 'Аккаунт активен',
    icon: <UserCheck className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'accountStatus', operator: 'eq', value: 'active' }],
    },
  },
];

// ── Продуктовые пресеты: курсы (продукт выбирается после добавления) ──────
const PRODUCT_PRESETS: Preset[] = [
  {
    label: 'Есть доступ к курсу',
    icon: <GraduationCap className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      // value не задаём — пользователь выберет конкретный курс в условии.
      conditions: [{ field: 'courseAccess', operator: 'hasActive' }],
    },
  },
  {
    label: 'Купил курс',
    icon: <CreditCard className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'coursePurchased', operator: 'purchased' }],
    },
  },
  {
    label: 'Курс пройден',
    icon: <GraduationCap className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'courseCompleted', operator: 'completed' }],
    },
  },
  {
    label: 'Курс не пройден',
    icon: <XCircle className="h-3.5 w-3.5" />,
    group: {
      conditionsOp: 'AND',
      conditions: [{ field: 'courseCompleted', operator: 'notCompleted' }],
    },
  },
];

// ── Динамические пресеты по абонементам/каналам ──────────────────────────

interface SubscriptionPlanLite {
  id: number;
  code: string;
  isActive: boolean;
  translations?: Record<string, { name?: string } | undefined>;
}

interface ChannelPassLite {
  id: number;
  isActive: boolean;
  translations?: Record<string, { name?: string } | undefined>;
}

function planName(plan: SubscriptionPlanLite): string {
  const ru = plan.translations?.['ru'];
  if (ru?.name) {
    return ru.name;
  }
  const firstKey = plan.translations ? Object.keys(plan.translations)[0] : undefined;
  const first = firstKey ? plan.translations?.[firstKey] : undefined;
  return first?.name ?? plan.code;
}

function passName(pass: ChannelPassLite): string {
  const ru = pass.translations?.['ru'];
  if (ru?.name) {
    return ru.name;
  }
  const firstKey = pass.translations ? Object.keys(pass.translations)[0] : undefined;
  const first = firstKey ? pass.translations?.[firstKey] : undefined;
  return first?.name ?? `Абонемент #${pass.id}`;
}

export interface SegmentPresetsProps {
  onApply: (group: SegmentGroup) => void;
  disabled?: boolean;
}

/**
 * Горизонтальный ряд кнопок-чипов готовых сценариев сегментации.
 * Клик добавляет готовую группу условий в конструктор.
 *
 * Помимо статических — подгружает активные абонементы (subscription-plans)
 * и канальные пропуска (channel-passes) и генерирует чип на каждый:
 * «Подписка: <план>» → активная подписка на этот план;
 * «Абонемент: <канал>» → активный доступ к каналу.
 */
export function SegmentPresets({ onApply, disabled = false }: SegmentPresetsProps) {
  const plansResult = useList<SubscriptionPlanLite>({
    resource: 'subscription-plans',
    pagination: { pageSize: 100 },
    queryOptions: { staleTime: 60_000 },
  });

  const passesResult = useList<ChannelPassLite>({
    resource: 'channel-passes',
    pagination: { pageSize: 100 },
    queryOptions: { staleTime: 60_000 },
  });

  // subscription-plans = «Абонементы» (доменный термин проекта, см. App.tsx)
  const planPresets: Preset[] = (plansResult.result?.data ?? [])
    .filter((p) => p.isActive)
    .map((p) => ({
      label: `Абонемент: ${planName(p)}`,
      icon: <Ticket className="h-3.5 w-3.5" />,
      group: {
        conditionsOp: 'AND',
        conditions: [
          { field: 'subscriptionStatus', operator: 'eq', value: 'ACTIVE' },
          { field: 'subscriptionPlanId', operator: 'in', value: [p.id] },
        ],
      },
    }));

  // channel-passes = «Подписки в канал» (доменный термин проекта)
  const passPresets: Preset[] = (passesResult.result?.data ?? [])
    .filter((p) => p.isActive)
    .map((p) => ({
      label: `Подписка в канал: ${passName(p)}`,
      icon: <Ticket className="h-3.5 w-3.5" />,
      group: {
        conditionsOp: 'AND',
        conditions: [{ field: 'channelAccess', operator: 'hasActive', value: { targetId: p.id } }],
      },
    }));

  const allPresets = [...STATIC_PRESETS, ...PRODUCT_PRESETS, ...planPresets, ...passPresets];

  return (
    <div className="flex flex-wrap gap-2">
      {allPresets.map((preset) => (
        <Button
          key={preset.label}
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onApply(preset.group)}
          className="gap-1.5 text-xs h-7"
        >
          {preset.icon}
          {preset.label}
        </Button>
      ))}
    </div>
  );
}
